/*
役所地番地図（TRUSTART 地番図データ）の pmtiles は CloudFront の署名付き URL
（canned policy）で配信され、クエリの Expires(UNIX秒) を過ぎると 403 になる。

失効の判定をここに集約している。使う側は2つある。
- useTrustartTile: 失効の手前で URL を再取得するための間隔計算
- TrustartChibanCoverageStrokeOverlay: 失効に起因する 403 を Sentry に送らない判定
片方だけ「失効」の基準が変わると、再取得は走らないのに通知だけ止まる（またはその逆）
といった食い違いが起きるため、両者は必ず同じ関数を通す。
*/

// 署名切れ前に URL を再取得するための余裕（秒）。
export const SIGNED_URL_REFRESH_MARGIN_SECONDS = 5 * 60;

// pmtiles は HTTP エラーを `Bad response code: <status>` というメッセージの
// Error で投げる（node_modules/pmtiles/dist/index.js）。型で判別できないため
// メッセージから status を取り出す。
const PMTILES_HTTP_ERROR_PATTERN = /Bad response code:\s*(\d{3})/;

/**
 * 署名付き URL の Expires(UNIX秒) を取り出す。
 * Expires を持たない URL（VITE_TRUSTART_PMTILES_URL によるローカル上書きなど）や
 * 数値として読めない場合は undefined を返す。
 */
export const getSignedUrlExpiresSeconds = (url: string): number | undefined => {
  const expiresParam = new URL(url, window.location.origin).searchParams.get(
    "Expires"
  );
  const expiresSeconds = expiresParam === null ? NaN : Number(expiresParam);
  return Number.isFinite(expiresSeconds) ? expiresSeconds : undefined;
};

/**
 * 署名 URL が「再取得されているべき時刻」を過ぎているか。
 *
 * 失効そのものではなく失効の SIGNED_URL_REFRESH_MARGIN_SECONDS 秒前を境にするのは、
 * useTrustartTile がその時点で再取得を走らせる設計だから。つまりここが true の間の
 * 403 は「新しい URL に差し替わるまでの一時的なもの」であり、端末の時計ずれも
 * このマージンで吸収できる。
 *
 * Expires を読めない URL は失効を判定しようがないので false（＝異常として扱う）。
 */
export const isSignedUrlStale = (url: string): boolean => {
  const expiresSeconds = getSignedUrlExpiresSeconds(url);
  if (expiresSeconds === undefined) {
    return false;
  }
  return (
    expiresSeconds - Date.now() / 1000 <= SIGNED_URL_REFRESH_MARGIN_SECONDS
  );
};

/**
 * pmtiles タイル取得の失敗が「署名切れによる 403」か。
 *
 * 署名切れの 403 は URL の再取得で自動的に回復する想定内の失敗なので、呼び出し側は
 * これを Sentry 通知の対象から外す。署名がまだ有効なうちの 403（署名鍵の失効・
 * 配信設定ミスなど）は回復しないため、この関数は false を返して通知させる。
 */
export const isSignedUrlExpiredForbiddenError = (
  error: unknown,
  url: string
): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  const matched = PMTILES_HTTP_ERROR_PATTERN.exec(error.message);
  if (matched === null || Number(matched[1]) !== 403) {
    return false;
  }
  return isSignedUrlStale(url);
};
